// Copyright 2026. WebPros International GmbH. All rights reserved.

import { IComputeResourceResponse } from 'common/api/resources/ComputeResource/model';
import {
    convertToDataUnit,
    DataUnit,
} from 'common/helpers/units';

/**
 * A compute resource's RAM capacity, in bytes.
 *
 * `settings.limits.ram` is an admin-configured allocation cap, not a hardware fact: until an
 * admin sets one it stays `{ unlimited: true, total: 0 }`, so it cannot stand in for capacity
 * on its own. Fall back to the host's physical memory as reported by the agent
 * (`metrics.memory.total`) whenever no limit is configured.
 *
 * `metrics` is persisted server-side and is not cleared when a compute resource stops
 * reporting, so an unavailable host still yields its last known memory. Returns 0 — never a
 * placeholder — for a host that has never reported any, so callers do not overstate capacity.
 */
export const getComputeResourceRamCapacity = (computeResource: IComputeResourceResponse): number =>
    computeResource.settings.limits.ram.unlimited
        ? getComputeResourceHostRam(computeResource)
        : computeResource.settings.limits.ram.total;

/**
 * The host's own physical memory, in bytes, or 0 when the compute resource has never reported it.
 */
export const getComputeResourceHostRam = (computeResource: IComputeResourceResponse): number =>
    computeResource.metrics?.memory?.total ?? 0;

/**
 * The host's own physical memory in whole and fractional GiB, for display.
 *
 * Deliberately not rounded: a nominally 4 GiB host reports 3909 MiB, and rounding that to `4`
 * would make RAM provisioned beyond the hardware look like an exact match. Returns 0 both when
 * nothing was ever reported and when the reported value is too small to show at this precision,
 * so callers have a single value to test before rendering it.
 */
export const getComputeResourceHostRamGiB = (computeResource: IComputeResourceResponse): number =>
    convertToDataUnit(getComputeResourceHostRam(computeResource), DataUnit.GiB);
